import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  Card,
  Row,
  Col,
  Typography,
  Button,
  Radio,
  Space,
  Divider,
  Spin,
  Alert,
  Tag,
  Statistic,
  message,
} from 'antd';
import {
  CreditCardOutlined,
  BarcodeOutlined,
  QrcodeOutlined,
  CheckCircleOutlined,
  ArrowLeftOutlined,
  DollarOutlined,
} from '@ant-design/icons';
import { supabase } from '../../../shared/lib/supabase';

const { Title, Text, Paragraph } = Typography;

type BillingType = 'CREDIT_CARD' | 'BOLETO' | 'PIX' | 'UNDEFINED';

export const CheckoutPage: React.FC = () => {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const [billingType, setBillingType] = useState<BillingType>('UNDEFINED');
  const [redirecting, setRedirecting] = useState(false);

  // Fetch product details
  const { data: product, isLoading: productLoading } = useQuery({
    queryKey: ['product', productId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('id', productId)
        .single();

      if (error) throw error;
      return data;
    },
  });

  // Create subscription mutation
  const createSubscriptionMutation = useMutation({
    mutationFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const payload: any = {
        product_id: productId,
        billing_type: billingType,
      };

      console.log('Creating payment link with payload:', payload);

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-subscription`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create subscription');
      }

      return response.json();
    },
    onSuccess: (data) => {
      console.log('Payment created:', data);
      
      const paymentUrl = data.payment_url || data.payment_link_url; // Backward compatibility
      if (paymentUrl) {
        message.success('Redirecionando para o pagamento...');
        setRedirecting(true);
        
        // Redirect to Asaas payment page
        setTimeout(() => {
          window.location.href = paymentUrl;
        }, 1500);
      } else {
        message.error('URL de pagamento não foi gerada');
      }
    },
    onError: (error: any) => {
      message.error(error.message || 'Erro ao criar assinatura');
    },
  });

  const handleSubmit = () => {
    createSubscriptionMutation.mutate();
  };

  if (productLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!product) {
    return (
      <Alert
        message="Produto não encontrado"
        description="O produto que você está tentando acessar não existe."
        type="error"
        showIcon
      />
    );
  }

  // Calculate values
  const isRecurring = product.monthly_price && product.monthly_price > 0;
  const firstPaymentTotal = (product.monthly_price || 0) + (product.setup_fee || 0);
  const nextPaymentsValue = product.monthly_price || 0;

  if (redirecting) {
    return (
      <div style={{ textAlign: 'center', padding: '100px' }}>
        <Spin size="large" />
        <Title level={4} style={{ marginTop: 24 }}>
          Redirecionando para o pagamento...
        </Title>
        <Paragraph>
          Você será redirecionado para finalizar o pagamento de forma segura no Asaas.
        </Paragraph>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px' }}>
      <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)} style={{ marginBottom: 24 }}>
        Voltar
      </Button>

      <Row gutter={24}>
        {/* Product Summary */}
        <Col xs={24} lg={8}>
          <Card>
            <Title level={4}>Resumo da Compra</Title>
            <Divider />

            <Space direction="vertical" style={{ width: '100%' }} size="large">
              <div>
                <Text type="secondary">Produto</Text>
                <Title level={5} style={{ margin: 0 }}>{product.name}</Title>
              </div>

              {product.description && (
                <div>
                  <Text type="secondary">Descrição</Text>
                  <Paragraph>{product.description}</Paragraph>
                </div>
              )}

              <Divider />

              {isRecurring ? (
                <>
                  {/* First Payment Total */}
                  <div style={{ background: '#f0f5ff', padding: 16, borderRadius: 8, border: '2px solid #1890ff' }}>
                    <Space direction="vertical" style={{ width: '100%' }} size="small">
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        1º Pagamento (Total)
                      </Text>
                      <Statistic
                        value={firstPaymentTotal}
                        precision={2}
                        prefix="R$"
                        valueStyle={{ color: '#1890ff', fontSize: 28, fontWeight: 'bold' }}
                      />
                      {product.setup_fee && product.setup_fee > 0 && (
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          R$ {product.monthly_price.toFixed(2)} (mensalidade) + R$ {product.setup_fee.toFixed(2)} (taxa de adesão)
                        </Text>
                      )}
                    </Space>
                  </div>

                  {/* Next Payments */}
                  <div style={{ background: '#f6ffed', padding: 16, borderRadius: 8, border: '1px solid #52c41a' }}>
                    <Space direction="vertical" style={{ width: '100%' }} size="small">
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Próximos Meses
                      </Text>
                      <Statistic
                        value={nextPaymentsValue}
                        precision={2}
                        prefix="R$"
                        suffix="/mês"
                        valueStyle={{ color: '#52c41a', fontSize: 24, fontWeight: 'bold' }}
                      />
                    </Space>
                  </div>

                  {product.trial_days && product.trial_days > 0 && (
                    <Alert
                      message={`${product.trial_days} dias de teste grátis`}
                      description="Você terá um período de teste antes da primeira cobrança."
                      type="success"
                      showIcon
                    />
                  )}

                  <Alert
                    message="Cobrança Recorrente"
                    description="Você será cobrado automaticamente todo mês até cancelar a assinatura."
                    type="info"
                    showIcon
                  />
                </>
              ) : (
                <>
                  <div style={{ background: '#f6ffed', padding: 16, borderRadius: 8, border: '2px solid #52c41a' }}>
                    <Space direction="vertical" style={{ width: '100%' }} size="small">
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Valor Total (Pagamento Único)
                      </Text>
                      <Statistic
                        value={product.setup_fee || 0}
                        precision={2}
                        prefix="R$"
                        valueStyle={{ color: '#52c41a', fontSize: 28, fontWeight: 'bold' }}
                      />
                    </Space>
                  </div>
                  
                  <Alert
                    message="Pagamento Único"
                    description="Este produto requer apenas um pagamento único, sem cobrança recorrente."
                    type="info"
                    showIcon
                  />
                </>
              )}
            </Space>
          </Card>
        </Col>

        {/* Payment Form */}
        <Col xs={24} lg={16}>
          <Card>
            <Title level={4}>Forma de Pagamento</Title>
            <Paragraph type="secondary">
              Você será redirecionado para uma página segura do Asaas para finalizar o pagamento.
            </Paragraph>
            <Divider />

            <Alert
              message="Pagamento 100% Seguro"
              description="Todas as transações são processadas de forma segura e criptografada pelo Asaas, um dos maiores gateways de pagamento do Brasil."
              type="success"
              showIcon
              icon={<CheckCircleOutlined />}
              style={{ marginBottom: 24 }}
            />

            <Radio.Group
              value={billingType}
              onChange={(e) => setBillingType(e.target.value)}
              style={{ marginBottom: 32, width: '100%' }}
            >
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                <Radio value="UNDEFINED">
                  <Space>
                    <DollarOutlined style={{ fontSize: 20 }} />
                    <div>
                      <div><strong>Escolher na próxima página</strong></div>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Ver todas as opções disponíveis: Cartão, Boleto ou PIX
                      </Text>
                    </div>
                  </Space>
                </Radio>

                <Radio value="CREDIT_CARD">
                  <Space>
                    <CreditCardOutlined style={{ fontSize: 20 }} />
                    <div>
                      <div>Cartão de Crédito</div>
                      <Tag color="green" style={{ marginTop: 4 }}>Ativação Imediata</Tag>
                    </div>
                  </Space>
                </Radio>

                <Radio value="PIX">
                  <Space>
                    <QrcodeOutlined style={{ fontSize: 20 }} />
                    <div>
                      <div>PIX</div>
                      <Tag color="blue" style={{ marginTop: 4 }}>Confirmação em minutos</Tag>
                    </div>
                  </Space>
                </Radio>

                <Radio value="BOLETO">
                  <Space>
                    <BarcodeOutlined style={{ fontSize: 20 }} />
                    <div>
                      <div>Boleto Bancário</div>
                      <Tag color="orange" style={{ marginTop: 4 }}>Confirmação em até 3 dias</Tag>
                    </div>
                  </Space>
                </Radio>
              </Space>
            </Radio.Group>

            <Divider />

            {isRecurring && (
              <Alert
                message="Como funciona a cobrança recorrente?"
                description={
                  <ul style={{ margin: '8px 0', paddingLeft: 20 }}>
                    <li>Primeiro mês: R$ {firstPaymentTotal.toFixed(2)} (mensalidade + taxa de adesão)</li>
                    <li>Próximos meses: R$ {nextPaymentsValue.toFixed(2)}/mês automaticamente</li>
                    <li>Você pode cancelar a qualquer momento sem multa</li>
                  </ul>
                }
                type="info"
                showIcon
                style={{ marginBottom: 24 }}
              />
            )}

            <div style={{ textAlign: 'center', marginTop: 32 }}>
              <Button
                type="primary"
                size="large"
                onClick={handleSubmit}
                loading={createSubscriptionMutation.isPending}
                style={{ minWidth: 200, height: 50, fontSize: 16 }}
              >
                {createSubscriptionMutation.isPending 
                  ? 'Gerando link de pagamento...' 
                  : 'Continuar para o Pagamento'}
              </Button>
              
              {isRecurring ? (
                <div style={{ marginTop: 16 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Ao clicar acima, você concorda com os termos de assinatura recorrente
                  </Text>
                </div>
              ) : (
                <div style={{ marginTop: 16 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Pagamento seguro processado pelo Asaas
                  </Text>
                </div>
              )}
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default CheckoutPage;
